import { computeAssembly } from "../engine/assembly";
import type { EntityChange, PlanModel, PlanObject } from "../geometry";
import type { DesignSyncService, UpstreamSyncOutcome } from "./designSyncService";
import { aggregateObjectQuantityX100, analyzePlan, setObjectFeature } from "../geometry";
import { effectiveFeatureQuantity, isObjectBinding, isRoomBinding, type FeatureAction, type FeatureGeometryBinding, type ProjectFeature } from "../domain/feature";
import type { ObjectDefinitionRepository } from "./repositories/objectDefinitionRepository";
import type { ScopeSession } from "../design/scopeSession";
import { effectiveFloorArea, effectivePerimeter, effectiveWallArea, type Room, type RoomGeometry } from "../domain/room";
import type { DbExecutor } from "./executor";
import type { PlanRepository } from "./repositories/planRepository";
import type { FeatureRepository } from "./repositories/featureRepository";
import type { RoomRepository } from "./repositories/roomRepository";
import type { AssemblyRepository } from "./repositories/assemblyRepository";

/**
 * DesignScopeCoordinator — the ONE place where floorplan geometry reaches
 * the structured project model (permanent requirement: Design and Scope &
 * Estimate are two views of the same model; React never calls five
 * repositories by hand).
 *
 *   PlanSession.commit(op)
 *        ↓ afterPlanCommit(model)
 *   1. analyze regions → update room_geometry cache (area / perimeter /
 *      status bound | unbound | conflict) for every anchored room
 *   2. refresh quantity_geometry_x100 on features bound to those rooms
 *   3. assemblies.regenerateForFeature(...) → generated lines reprice
 *      (the BOM/product hook runs inside regeneration)
 *
 * Everything is synchronous and deterministic; nothing here is UI.
 */
/** A floor's plan and its region analysis, resolved once per reconcile. */
interface FloorContext {
  model: PlanModel;
  analysis: ReturnType<typeof analyzePlan>;
}

export class DesignScopeCoordinator {
  private listeners = new Set<(event: ScopeSyncEvent) => void>();
  /** Phase 9 change-analysis reconciler (set after construction to avoid a
   *  circular dependency); afterPlanCommit runs it inside the same flow. */
  private changes: { reconcileAfterCommit(designId: string): Promise<string[]> } | null = null;
  /** Existing → Proposed inheritance (spec §60H); set after construction. */
  private sync: DesignSyncService | null = null;
  /** Doors replaced in place (spec §85); set after construction. */
  private openingReplacements: { reconcile(designId: string): Promise<string[]> } | null = null;

  private automaticFinishes: ((projectId: string, designId: string) => Promise<string[]>) | null = null;
  setAutomaticFinishReconciler(reconciler: (projectId: string, designId: string) => Promise<string[]>): void { this.automaticFinishes = reconciler; }

  setOpeningReplacements(service: { reconcile(designId: string): Promise<string[]> }): void {
    this.openingReplacements = service;
  }

  setSyncService(sync: DesignSyncService): void {
    this.sync = sync;
  }

  setChangeCoordinator(changes: { reconcileAfterCommit(designId: string): Promise<string[]> }): void {
    this.changes = changes;
  }

  /** The scope-source design id for the project owning `designId`. */
  private async activeDesignIdForDesign(designId: string): Promise<string | null> {
    const rows = await this.db.select<{ active_design_id: string | null }>(
      "SELECT p.active_design_id FROM projects p JOIN designs d ON d.project_id = p.id WHERE d.id = ?",
      [designId],
    );
    return rows[0]?.active_design_id ?? null;
  }

  /** After the scope design changes (or its geometry): recompute EVERY bound feature of the project. */
  async refreshProjectScope(projectId: string): Promise<void> {
    const rooms = await this.db.select<{ id: string }>(
      "SELECT r.id FROM rooms r JOIN floors f ON f.id = r.floor_id WHERE f.project_id = ? AND r.archived_at IS NULL",
      [projectId],
    );
    const featureIds = await this.refreshBoundFeatures(rooms.map((r) => r.id));
    this.emit({ floorId: null, changedRoomIds: rooms.map((r) => r.id), regeneratedFeatureIds: featureIds });
  }

  constructor(
    private readonly db: DbExecutor,
    private readonly plans: PlanRepository,
    private readonly rooms: RoomRepository,
    private readonly features: FeatureRepository,
    private readonly assemblies: AssemblyRepository,
    private readonly objectDefs: ObjectDefinitionRepository,
  ) {}

  /** Other workspaces subscribe to learn that scope/estimate changed. */
  subscribe(listener: (event: ScopeSyncEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: ScopeSyncEvent): void {
    for (const l of this.listeners) l(event);
  }

  /** Called by PlanSession after every committed geometry operation (and undo/redo). */
  /** `touchedRoomIds`: rooms whose anchors were changed/removed by the committed operations (their cache rows may already be gone). */
  async afterPlanCommit(
    model: PlanModel,
    touchedRoomIds: Iterable<string> = [],
    /** The changeset just applied, so an Existing edit can flow forward. */
    appliedChanges: readonly EntityChange[] = [],
  ): Promise<ScopeSyncEvent> {
    const analysis = analyzePlan(model);
    const previous = new Map((await this.plans.listRoomGeometryByFloor(model.designId, model.floorId)).map((g) => [g.roomId, g]));
    const changedRoomIds: string[] = [];
    for (const anchor of model.anchors.values()) {
      const key = analysis.roomRegion.get(anchor.roomId);
      const region = key ? analysis.regions.find((r) => r.key === key) ?? null : null;
      const status: RoomGeometry["status"] = !region ? "unbound" : region.status === "conflict" ? "conflict" : "bound";
      const values = {
        floorAreaX100: status === "bound" ? region!.floorAreaX100 : null,
        perimeterX100: status === "bound" ? region!.perimeterX100 : null,
        status,
        polygonJson: region ? JSON.stringify(region.interior) : null,
      };
      const prev = previous.get(anchor.roomId);
      await this.plans.updateRoomGeometryCache(model.designId, anchor.roomId, values);
      if (!prev || prev.floorAreaX100 !== values.floorAreaX100 || prev.perimeterX100 !== values.perimeterX100 || prev.status !== values.status) {
        changedRoomIds.push(anchor.roomId);
      }
    }
    // Rooms whose anchor was removed this commit (cache rows already deleted by the repository).
    for (const roomId of previous.keys()) if (!model.anchors.has(roomId) && !changedRoomIds.includes(roomId)) changedRoomIds.push(roomId);
    for (const roomId of touchedRoomIds) if (!changedRoomIds.includes(roomId)) changedRoomIds.push(roomId);
    // Scope quantities follow ONE design (projects.active_design_id). Edits
    // to any other scenario update that scenario's cache only.
    const activeDesignId = await this.activeDesignIdForDesign(model.designId);
    const featureIds = model.designId === activeDesignId ? await this.refreshBoundFeatures(changedRoomIds) : [];
    // Object-bound features belong to THIS design; refresh them on every commit
    // (add/move/resize/delete/clear all change aggregations).
    featureIds.push(...(await this.reconcileObjectFeatures(model.designId, { model, analysis })));
    /* Spec §85: a door that has been deleted stops being replaced, and the
     * count follows the doors that are actually drawn. */
    if (this.openingReplacements) {
      try {
        featureIds.push(...(await this.openingReplacements.reconcile(model.designId)));
      } catch (err) {
        console.error("Door-replacement reconcile failed:", err);
      }
    }
    // Phase 9: recompute the change analysis (Existing edits reconcile every
    // proposed design; proposed edits reconcile their own).
    if (this.changes) {
      try {
        featureIds.push(...(await this.changes.reconcileAfterCommit(model.designId)));
      } catch (err) {
        console.error("Change-analysis reconcile failed:", err);
      }
    }
    // Spec §60H: a discovery about the EXISTING house flows forward into
    // every plan that has no opinion about it; anything a plan decided for
    // itself is left alone and reported instead.
    let upstream: UpstreamSyncOutcome | null = null;
    if (this.sync && appliedChanges.length > 0) {
      try {
        const design = await this.db.select<{ project_id: string; kind: string }>(
          "SELECT project_id, kind FROM designs WHERE id = ?",
          [model.designId],
        );
        if (design[0]?.kind === "existing") {
          upstream = await this.sync.propagateFromExisting(
            design[0].project_id,
            model.designId,
            model.floorId,
            appliedChanges,
          );
        }
      } catch (err) {
        console.error("Existing → Proposed sync failed:", err);
      }
    }
    if (this.automaticFinishes) {
      const owner = await this.db.select<{ project_id: string; kind: string }>("SELECT project_id, kind FROM designs WHERE id = ?", [model.designId]);
      if (owner[0]?.kind === "proposed") featureIds.push(...await this.automaticFinishes(owner[0].project_id, model.designId));
    }
    const event: ScopeSyncEvent = {
      floorId: model.floorId,
      changedRoomIds,
      regeneratedFeatureIds: featureIds,
      upstream,
    };
    this.emit(event);
    return event;
  }

  /**
   * Reconcile every object-bound feature of a design: quantity = aggregate
   * over its ACTIVE bound objects (count / run LF / area sqft). When the
   * last object disappears: an auto-created, untouched, financially
   * unreferenced feature is archived with it; anything touched survives
   * with the geometry quantity nulled and is surfaced in Scope & Estimate.
   */
  async reconcileObjectFeatures(designId: string, context?: FloorContext): Promise<string[]> {
    /* WHERE THE WORK IS is derived from the objects, on every commit —
     * never captured once when the object happened to be dropped.
     *
     * Placing a toilet while the walls around it are still moving used to
     * record "no room" FOREVER: room detection had not caught up, so the
     * feature was born project-wide and nothing ever asked again. The
     * geometry is the authority, so the answer is re-derived here (spec
     * §84-1). Rooms cost nothing to change: no money, quantity, category
     * or assembly depends on this column. */
    const floors = new Map<string, FloorContext>();
    if (context) floors.set(context.model.floorId, context);
    const rows = await this.db.select<{ id: string; quantity_geometry_binding: string; quantity_geometry_x100: number | null; quantity_x100: number | null; archived_at: string | null; room_id: string | null }>(
      `SELECT id, quantity_geometry_binding, quantity_geometry_x100, quantity_x100, archived_at, room_id FROM project_features
        WHERE design_id = ? AND quantity_geometry_binding IN ('objects-count','objects-run-length','objects-area') AND archived_at IS NULL`,
      [designId],
    );
    // Resurrect auto-archived features whose objects came BACK (undo of a
    // delete or Clear Design): a live object bound to an archived feature is
    // the exact inverse of the archive-with-last-object rule.
    const revived = await this.db.select<{ id: string; quantity_geometry_binding: string; quantity_geometry_x100: number | null; quantity_x100: number | null; archived_at: string | null; room_id: string | null }>(
      `SELECT id, quantity_geometry_binding, quantity_geometry_x100, quantity_x100, archived_at, room_id FROM project_features f
        WHERE design_id = ? AND quantity_geometry_binding IN ('objects-count','objects-run-length','objects-area') AND archived_at IS NOT NULL
          AND EXISTS (SELECT 1 FROM plan_objects o WHERE o.feature_id = f.id AND o.archived_at IS NULL)`,
      [designId],
    );
    for (const row of revived) {
      const now = new Date().toISOString();
      await this.db.execute("UPDATE project_features SET archived_at = NULL, updated_at = ? WHERE id = ?", [now, row.id]);
      rows.push({ ...row, archived_at: null });
    }
    const touched: string[] = [];
    for (const row of rows) {
      const objects = await this.plans.listObjectsForFeature(row.id);
      if (objects.length > 0) {
        const room = await this.deriveFeatureRoom(designId, objects, floors);
        if (room !== row.room_id) {
          await this.db.execute("UPDATE project_features SET room_id = ?, updated_at = ? WHERE id = ?", [room, new Date().toISOString(), row.id]);
          if (!touched.includes(row.id)) touched.push(row.id);
        }
      }
      const rule = row.quantity_geometry_binding === "objects-count" ? "count" : row.quantity_geometry_binding === "objects-run-length" ? "run-length" : "area";
      const value = aggregateObjectQuantityX100(objects, rule);
      if (value === null) {
        if (await this.isAutoRemovable(row.id)) {
          const now = new Date().toISOString();
          await this.db.execute("UPDATE project_features SET archived_at = ?, updated_at = ?, quantity_geometry_x100 = NULL WHERE id = ?", [now, now, row.id]);
          await this.archiveGeneratedLines(row.id, now);
          touched.push(row.id);
          continue;
        }
        if (row.quantity_geometry_x100 !== null) {
          await this.features.setGeometryQuantity(row.id, null);
          try {
            await this.assemblies.regenerateForFeature(row.id);
          } catch { /* surfaced in the assembly panel */ }
          touched.push(row.id);
        }
        continue;
      }
      if (value !== row.quantity_geometry_x100) {
        await this.features.setGeometryQuantity(row.id, value);
        try {
          await this.assemblies.regenerateForFeature(row.id);
        } catch { /* surfaced in the assembly panel */ }
        touched.push(row.id);
      }
    }
    return touched;
  }

  /** Approved rule: archive with the last object only if auto-created & untouched & financially unreferenced. */
  async isAutoRemovable(featureId: string): Promise<boolean> {
    const q = async (sql: string): Promise<number> => Number((await this.db.select<{ n: number }>(sql, [featureId]))[0]?.n ?? 0);
    if ((await q("SELECT COUNT(*) AS n FROM project_features WHERE id = ? AND (quantity_x100 IS NOT NULL OR notes <> '' OR work_status IS NOT NULL)")) > 0) return false; // manual override = touched
    if ((await q("SELECT COUNT(*) AS n FROM actual_expenses WHERE feature_id = ?")) > 0) return false;
    if ((await q("SELECT COUNT(*) AS n FROM actual_expenses WHERE linked_proposed_line_id IN (SELECT id FROM proposed_cost_lines WHERE feature_id = ?)")) > 0) return false;
    if ((await q("SELECT COUNT(*) AS n FROM proposed_cost_lines WHERE feature_id = ? AND source = 'manual' AND archived_at IS NULL")) > 0) return false;
    // Linked money, issued decisions and copied history outlive their geometry.
    if (await q("SELECT COUNT(*) AS n FROM purchase_allocations WHERE linked_feature_id = ?")) {
      return false;
    }
    if (await q("SELECT COUNT(*) AS n FROM purchase_allocations WHERE linked_proposed_line_id IN (SELECT id FROM proposed_cost_lines WHERE feature_id = ?)")) return false;
    if (await q("SELECT COUNT(*) AS n FROM baseline_lines WHERE feature_id = ?")) return false;
    if (await q("SELECT COUNT(*) AS n FROM change_order_lines WHERE feature_id = ?")) return false;
    if (await q("SELECT COUNT(*) AS n FROM assembly_instances WHERE project_feature_id = ? AND (updated_at <> created_at OR template_id IS NULL OR notes <> '')")) return false;
    if (await q("SELECT COUNT(*) AS n FROM assembly_instance_components c JOIN assembly_instances i ON i.id = c.instance_id WHERE i.project_feature_id = ? AND (c.updated_at <> c.created_at OR c.archived_at IS NOT NULL OR (c.qty_override_x100 IS NOT NULL AND c.automatic_demolition_default IS NULL))")) return false;
    // Copies receive new audit timestamps. Compare their copied choices with
    // the recipe too, so a cloned user-edited price/component stays protected.
    // A later library change can conservatively retain work, never destroy it.
    if (await q(`SELECT COUNT(*) AS n FROM assembly_instance_components c JOIN assembly_instances i ON i.id = c.instance_id
      WHERE i.project_feature_id = ? AND NOT EXISTS (
        SELECT 1 FROM assembly_template_components t WHERE t.template_id = i.template_id AND t.name = c.name
        AND t.unit_cost_cents = c.unit_cost_cents AND COALESCE(t.labor_minutes_x100, -1) = COALESCE(c.labor_minutes_x100, -1)
        AND t.qty_basis = c.qty_basis AND t.fixed_qty_x100 = c.fixed_qty_x100 AND t.multiplier_x10000 = c.multiplier_x10000
        AND t.waste_pct_x100 = c.waste_pct_x100 AND t.unit = c.unit AND t.notes = c.notes
        AND (c.automatic_demolition_default IS NOT NULL OR (t.inclusion = c.inclusion AND t.default_included = c.included)))`)) return false;
    if (await q(`SELECT COUNT(*) AS n FROM assembly_instance_options o JOIN assembly_instances i ON i.id = o.instance_id
      WHERE i.project_feature_id = ? AND o.automatic_finish_default IS NULL
      AND NOT EXISTS (SELECT 1 FROM assembly_instance_components c WHERE c.condition_option_id = o.id AND c.automatic_demolition_default IS NOT NULL)
      AND NOT EXISTS (SELECT 1 FROM assembly_template_options t WHERE t.template_id = i.template_id AND t.prompt = o.prompt AND t.default_value = o.value)`)) return false;
    if (await q("SELECT COUNT(*) AS n FROM estimate_evidence_decisions WHERE component_id IN (SELECT c.id FROM assembly_instance_components c JOIN assembly_instances i ON i.id = c.instance_id WHERE i.project_feature_id = ?)")) return false;
    if (await q("SELECT COUNT(*) AS n FROM project_product_selections WHERE component_id IN (SELECT c.id FROM assembly_instance_components c JOIN assembly_instances i ON i.id = c.instance_id WHERE i.project_feature_id = ?)")) return false;
    // Quote coverage and category authorizations can span work items: keep the
    // linked project's scope rather than guess an allocation of that decision.
    if (await q("SELECT COUNT(*) AS n FROM bid_packages WHERE project_id = (SELECT project_id FROM project_features WHERE id = ?)")) return false;
    if (await q("SELECT COUNT(*) AS n FROM contractor_quotes WHERE project_id = (SELECT project_id FROM project_features WHERE id = ?)")) return false;
    if (await q("SELECT COUNT(*) AS n FROM category_budgets WHERE project_id = (SELECT project_id FROM project_features WHERE id = ?) AND authorized_cents IS NOT NULL")) return false;
    return true;
  }

  async archiveGeneratedLines(featureId: string, now: string): Promise<void> {
    await this.db.execute("UPDATE proposed_cost_lines SET archived_at = COALESCE(archived_at, ?), updated_at = ? WHERE feature_id = ? AND source = 'assembly'", [now, now, featureId]);
  }

  /**
   * Place-object scope automation (Phase 8, approved decision 3): create or
   * reuse a Feature for a just-placed object, bind it, and auto-attach the
   * assembly when exactly one template is suggested. Runs ATOMICALLY with
   * the object's persistence (executor transactions are reentrant).
   * Never called for the Existing design.
   */
  async bindObjectScope(
    session: ScopeSession,
    objectId: string,
    options: { action: FeatureAction; existingFeatureId?: string | null },
  ): Promise<{ featureId: string; featureName: string; autoAttached: boolean } | null> {
    return this.db.transaction(async () => {
      await session.flush();
      const model = session.snapshot().model;
      const object = model.objects.get(objectId);
      if (!object || object.archivedAt !== null) return null;
      const definition = await this.objectDefs.getById(object.definitionId);
      if (!definition) return null;
      // Furniture is drawn to understand the room, never bought (spec §61J).
      // Dropping a sofa into a living room must not put a sofa in the
      // renovation estimate, so a 'visual' object creates no scope at all.
      if (definition.costBehavior === "visual") return null;
      const design = await this.db.select<{ kind: string; project_id: string }>("SELECT kind, project_id FROM designs WHERE id = ?", [model.designId]);
      if (!design[0] || design[0].kind === "existing") return null; // Existing documents facts — never scope
      const projectId = design[0].project_id;
      const analysis = session.snapshot().analysis;
      const containedRoom = this.roomOfObject(model, analysis, object);
      let feature: ProjectFeature;
      let autoAttached = false;
      if (options.existingFeatureId) {
        const existing = await this.features.getById(options.existingFeatureId);
        if (!existing) throw new Error("That feature no longer exists.");
        if (existing.designId !== null && existing.designId !== model.designId) {
          throw new Error("Objects can only merge into a feature of the same plan.");
        }
        // Spatial routing (spec §18J): a feature created from Scope &
        // Estimate arrives design-less; placing its object ADOPTS it into
        // this design and binds its quantity to the placed object(s) — the
        // SAME planning intent, never a duplicate.
        if (existing.designId === null) {
          await this.features.setDesign(existing.id, model.designId);
        }
        if (!isObjectBinding(existing.quantityGeometryBinding)) {
          // Spec §89: "24 lf" typed before anything was drawn is a decision,
          // not a placeholder — the placed objects' measurement sits beside it.
          await this.features.keepTypedQuantityAsOverride(existing.id);
          const kind: FeatureGeometryBinding =
            definition.quantityRule === "run-length" ? "objects-run-length" : definition.quantityRule === "area" ? "objects-area" : "objects-count";
          await this.features.setGeometryBinding(existing.id, kind, null);
        }
        feature = (await this.features.getById(existing.id))!;
      } else {
        const suggestions = await this.objectDefs.listSuggestedFeatureDefinitions(definition.id);
        const featureDefinition = suggestions[0] ?? null;
        const bindingKind: FeatureGeometryBinding =
          definition.quantityRule === "run-length" ? "objects-run-length" : definition.quantityRule === "area" ? "objects-area" : "objects-count";
        // PER-ROOM aggregation (approved decision 4): lights/fans/outlets/
        // switches in the same room join ONE feature per (design, room,
        // definition, action) instead of one feature per device.
        if (definition.aggregation === "per-room" && containedRoom && featureDefinition) {
          const reusable = await this.db.select<{ id: string }>(
            `SELECT f.id FROM project_features f
              WHERE f.design_id = ? AND f.room_id = ? AND f.feature_definition_id = ? AND f.action = ?
                AND f.quantity_geometry_binding = ? AND f.archived_at IS NULL
              ORDER BY f.created_at LIMIT 1`,
            [model.designId, containedRoom, featureDefinition.id, options.action, bindingKind],
          );
          if (reusable.length > 0) {
            const existing = await this.features.getById(reusable[0].id);
            if (existing) {
              session.commit("Bind scope", (m, ctx) => setObjectFeature(m, ctx, objectId, existing.id));
              await session.flush();
              const ids = await this.reconcileObjectFeatures(model.designId);
              const attached = await this.ensureDefaultAssembly(existing.id);
              this.emit({ floorId: model.floorId, changedRoomIds: [], regeneratedFeatureIds: [existing.id, ...ids] });
              return { featureId: existing.id, featureName: existing.name, autoAttached: attached };
            }
          }
        }
        const actionName = options.action === "install" ? "Install" : options.action === "replace" ? "Replace" : "Keep";
        const baseName = featureDefinition?.displayName ?? definition.displayName;
        const name = /replace|install/i.test(baseName) ? baseName.replace(/^(Replace|Install)/i, actionName) : `${actionName} ${definition.displayName}`;
        feature = await this.features.create(projectId, {
          designId: model.designId,
          roomId: containedRoom,
          floorId: null,
          featureDefinitionId: featureDefinition?.id ?? null,
          name,
          action: options.action,
          quantityX100: null,
          quantityUnit: null,
          notes: "",
        });
        await this.features.setGeometryBinding(feature.id, bindingKind, null);
      }
      // Link the object FIRST so the aggregated quantity exists before the
      // assembly attaches (a per-LF/sqft assembly needs its driving quantity).
      session.commit("Bind scope", (m, ctx) => setObjectFeature(m, ctx, objectId, feature.id));
      await session.flush();
      const ids = await this.reconcileObjectFeatures(model.designId);
      try { autoAttached = await this.ensureDefaultAssembly(feature.id); }
      catch (err) { console.error("Auto-attach failed:", err); }
      if (this.changes) ids.push(...await this.changes.reconcileAfterCommit(model.designId));
      this.emit({ floorId: model.floorId, changedRoomIds: [], regeneratedFeatureIds: [feature.id, ...ids] });
      return { featureId: feature.id, featureName: feature.name, autoAttached };
    });
  }

  /** Reconcile only the removal choices this automatic pipeline owns.
   * Pairing comes from the pure diff; explicit component/assembly edits freeze it. */
  async reconcileAutomaticReplacementRemoval(designId: string, pairs: ReadonlyMap<string, string>): Promise<string[]> {
    const rows = await this.db.select<{ id: string; instance_id: string; automatic_demolition_default: number; feature_id: string }>(
      `SELECT c.id, c.instance_id, c.automatic_demolition_default, f.id AS feature_id
       FROM assembly_instance_components c JOIN assembly_instances i ON i.id = c.instance_id
       JOIN project_features f ON f.id = i.project_feature_id
       WHERE f.design_id = ? AND f.archived_at IS NULL AND i.archived_at IS NULL
       AND c.archived_at IS NULL AND c.automatic_demolition_default IS NOT NULL AND c.updated_at = c.created_at AND i.updated_at = i.created_at`, [designId]);
    const touched = new Set<string>();
    const instances = new Map<string, Awaited<ReturnType<AssemblyRepository["getInstance"]>>>();
    const pairedByFeature = new Map<string, PlanObject[]>();
    // Installation measures the proposal; demolition measures what is
    // actually there. A longer new cabinet run never invents old cabinets.
    const existingObjects = new Map((await this.plans.listObjectsByIds([...pairs.values()])).map(o => [o.id, o]));
    for (const row of rows) {
      if (!pairedByFeature.has(row.feature_id)) pairedByFeature.set(row.feature_id, (await this.plans.listObjectsForFeature(row.feature_id)).flatMap(o => {
        const oldId = pairs.get(o.id), old = oldId ? existingObjects.get(oldId) : null;
        return old ? [old] : [];
      }));
      const paired = pairedByFeature.get(row.feature_id)!;
      const enabled = paired.length > 0;
      if (!instances.has(row.instance_id)) instances.set(row.instance_id, await this.assemblies.getInstance(row.instance_id));
      const instance = instances.get(row.instance_id);
      const component = instance?.components.find((c) => c.id === row.id);
      if (!instance || !component) continue;
      const quantity = aggregateObjectQuantityX100(paired, instance.drivingUnit === "lf" ? "run-length" : instance.drivingUnit === "sqft" ? "area" : "count");
      const calculation = computeAssembly(instance, { valueX100: quantity ?? 0, unit: instance.drivingUnit, source: "replacement pairing" }, null);
      const replacementQuantity = enabled ? calculation.components.find((c) => c.componentId === row.id)!.calculatedQtyX100 : null;
      if (row.automatic_demolition_default === Number(enabled) && component.qtyOverrideX100 === replacementQuantity) continue;
      if (component.conditionOptionId) await this.db.execute("UPDATE assembly_instance_options SET value = ? WHERE id = ?", [Number(enabled), component.conditionOptionId]);
      else await this.db.execute("UPDATE assembly_instance_components SET included = ? WHERE id = ?", [Number(enabled), row.id]);
      await this.db.execute("UPDATE assembly_instance_components SET automatic_demolition_default = ?, qty_override_x100 = ? WHERE id = ?", [Number(enabled), replacementQuantity, row.id]);
      touched.add(row.feature_id);
    }
    for (const id of touched) await this.assemblies.regenerateForFeature(id);
    return [...touched];
  }

  /** Unbind an object from its feature (then reconcile the feature). */
  async unbindObjectScope(session: ScopeSession, objectId: string): Promise<void> {
    await this.db.transaction(async () => {
      const model = session.snapshot().model;
      const object = model.objects.get(objectId);
      if (!object) return;
      session.commit("Remove scope", (m, ctx) => setObjectFeature(m, ctx, objectId, null));
      await session.flush();
      const ids = await this.reconcileObjectFeatures(model.designId);
      this.emit({ floorId: model.floorId, changedRoomIds: [], regeneratedFeatureIds: ids });
    });
  }

  /**
   * Which room each object of a design sits in, using the SAME containment
   * rule scope binding uses (Phase 10 needs this without a plan session, to
   * tell a package that a room already has a toilet). Geometry knowledge
   * stays here rather than being re-derived by callers.
   */
  async roomIdsForObjects(designId: string, floorId: string): Promise<Map<string, string | null>> {
    const model = await this.plans.loadFloor(designId, floorId);
    const analysis = analyzePlan(model);
    const out = new Map<string, string | null>();
    for (const object of model.objects.values()) {
      if (object.archivedAt !== null) continue;
      out.set(object.id, this.roomOfObject(model, analysis, object));
    }
    return out;
  }

  /**
   * The room a set of bound objects is IN.
   *
   * One room for all of them ⇒ that room. Objects in DIFFERENT rooms, or
   * in none that geometry can name, ⇒ null: work that genuinely spans the
   * house belongs at project level, and a guess between two rooms would be
   * worse than the honest answer (spec §84-2).
   */
  private async deriveFeatureRoom(
    designId: string,
    objects: readonly PlanObject[],
    floors: Map<string, FloorContext>,
  ): Promise<string | null> {
    const rooms = new Set<string>();
    for (const object of objects) {
      if (object.archivedAt !== null) continue;
      const pinned = object.roomId;
      if (pinned) {
        rooms.add(pinned);
        continue;
      }
      let floor = floors.get(object.floorId);
      if (!floor) {
        const model = await this.plans.loadFloor(designId, object.floorId);
        floor = { model, analysis: analyzePlan(model) };
        floors.set(object.floorId, floor);
      }
      const room = this.roomOfObject(floor.model, floor.analysis, object);
      if (room) rooms.add(room);
      else return null; // an object nobody can place keeps the whole item project-wide
    }
    return rooms.size === 1 ? [...rooms][0] : null;
  }

  private roomOfObject(_model: PlanModel, analysis: ReturnType<typeof analyzePlan>, o: PlanObject): string | null {
    if (o.roomId) return o.roomId;
    // containment via the analysis (single-room regions only)
    for (const r of analysis.regions) {
      if (r.roomIds.length !== 1) continue;
      // point-in-polygon over centerline loop
      const c = o.geometryType === "point" ? { x: o.x, y: o.y } : { x: Math.round((o.x + (o.endX ?? o.x)) / 2), y: Math.round((o.y + (o.endY ?? o.y)) / 2) };
      let inside = false;
      const poly = r.centerline;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[i];
        const b = poly[j];
        if (a.y > c.y !== b.y > c.y && c.x < ((b.x - a.x) * (c.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
      }
      if (inside) return r.roomIds[0];
    }
    return null;
  }

  /** Recompute ROOM-geometry quantities for bound features of the given rooms
   *  and regenerate their assemblies. OBJECT-bound features are owned by
   *  reconcileObjectFeatures and are never touched here (their quantity is
   *  an object aggregate, not a room measurement). */
  private async refreshBoundFeatures(roomIds: string[]): Promise<string[]> {
    if (roomIds.length === 0) return [];
    const bound = (await this.features.listGeometryBoundForRooms(roomIds)).filter((f) => isRoomBinding(f.quantityGeometryBinding));
    const regenerated: string[] = [];
    for (const f of bound) {
      const room = await this.rooms.getById(f.roomId!);
      const geometry = await this.plans.getActiveRoomGeometry(f.roomId!);
      const value = boundQuantityValue(room, geometry, f.quantityGeometryBinding!, room ? await this.floorWallHeight(room.floorId) : null);
      if (value !== f.quantityGeometryX100) {
        await this.features.setGeometryQuantity(f.id, value);
      }
      try {
        await this.assemblies.regenerateForFeature(f.id);
      } catch {
        // Unresolvable assemblies (e.g. unit mismatch) are surfaced by the assembly panel; never block geometry.
      }
      regenerated.push(f.id);
    }
    return regenerated;
  }

  /** Bind a room feature's quantity to its room geometry (the Phase 7 proof of concept). */
  /** Canonical copy-on-use pricing choice (§92): preserve any existing
   * instance, otherwise prefer a compatible project standard specification,
   * then the single compatible library suggestion. Ambiguity stays unknown.
   * New installation recipes do not assume an old fixture needs demolition. */
  async ensureDefaultAssembly(featureId: string): Promise<boolean> {
    const feature = await this.features.getById(featureId);
    if (!feature || feature.archivedAt !== null || !feature.featureDefinitionId) return false;
    const quantity = effectiveFeatureQuantity(feature);
    if (quantity.valueX100 === null || quantity.unit === null) return false;
    if ((await this.assemblies.listByFeature(featureId)).length > 0) return false;
    const compatible = (await this.assemblies.listSuggestedTemplates(feature.featureDefinitionId)).filter(
      (template) => template.drivingUnit === quantity.unit,
    );
    // Preserve copied instances above; project specifications precede library defaults.
    const spec = this.standardSpec ? await this.standardSpec(feature.projectId, feature.featureDefinitionId) : null;
    const named = spec?.assemblyTemplateId ?? null;
    const specified = named ? await this.assemblies.getTemplate(named) : null;
    let chosen = specified && specified.archivedAt === null && specified.drivingUnit === quantity.unit ? specified.id : null;
    if (chosen === null && compatible.length === 1) chosen = compatible[0].id;
    if (chosen === null) return false;
    const instance = await this.assemblies.attachTemplate(featureId, chosen);
    // New work documents no old fixture. Copy replacement recipes without
    // their demolition choices; later explicit edits remain untouched.
    if (feature.action === "install") {
      for (const component of instance.components.filter((c) => c.role === "demolition")) {
        if (component.conditionOptionId) {
          await this.db.execute("UPDATE assembly_instance_options SET value = 0 WHERE id = ?", [component.conditionOptionId]);
          await this.db.execute("UPDATE assembly_instance_components SET automatic_demolition_default = 0 WHERE id = ?", [component.id]);
        } else {
          await this.db.execute("UPDATE assembly_instance_components SET inclusion = 'optional', included = 0, automatic_demolition_default = 0 WHERE id = ?", [component.id]);
        }
      }
      await this.assemblies.regenerateForFeature(featureId);
    }
    this.emit({ floorId: null, changedRoomIds: [], regeneratedFeatureIds: [featureId] });
    return true;
  }

  /**
   * `attachDefaultAssembly: false` is for a caller that attaches the assembly
   * itself straight afterwards (package application, §88B). Without it, a
   * measured room gave the feature its default assembly here AND the
   * package's assembly a moment later — two instances, the work priced twice.
   */
  async bindFeatureQuantity(
    featureId: string,
    binding: FeatureGeometryBinding | null,
    options: { attachDefaultAssembly?: boolean } = {},
  ): Promise<void> {
    const feature = await this.features.getById(featureId);
    if (!feature) return;
    let value: number | null = null;
    if (binding && feature.roomId) {
      const room = await this.rooms.getById(feature.roomId);
      value = boundQuantityValue(room, await this.plans.getActiveRoomGeometry(feature.roomId), binding, room ? await this.floorWallHeight(room.floorId) : null);
    }
    await this.features.setGeometryBinding(featureId, binding, value);
    if (options.attachDefaultAssembly !== false) await this.ensureDefaultAssembly(featureId);
    try {
      await this.assemblies.regenerateForFeature(featureId);
    } catch {
      /* surfaced in the assembly panel */
    }
    this.emit({ floorId: null, changedRoomIds: [], regeneratedFeatureIds: [featureId] });
  }

  /** Manual override on a bound feature (null clears it; the calculated value stays visible). */
  async setFeatureQuantityOverride(featureId: string, valueX100: number | null): Promise<void> {
    await this.features.setQuantityOverride(featureId, valueX100);
    await this.ensureDefaultAssembly(featureId);
    try {
      await this.assemblies.regenerateForFeature(featureId);
    } catch {
      /* surfaced in the assembly panel */
    }
    this.emit({ floorId: null, changedRoomIds: [], regeneratedFeatureIds: [featureId] });
  }

  /** Project-owned snapshot specification; later library edits never reprice
   * an existing copy. Injected to preserve the service dependency boundary. */
  private standardSpec: ((projectId: string, featureDefinitionId: string) => Promise<{ assemblyTemplateId: string | null } | null>) | null = null;

  setStandardSpecResolver(resolver: (projectId: string, featureDefinitionId: string) => Promise<{ assemblyTemplateId: string | null } | null>): void {
    this.standardSpec = resolver;
  }

  /** The floor's default wall height, when somebody set one (spec §89 wall area). */
  private async floorWallHeight(floorId: string): Promise<number | null> {
    const rows = await this.db.select<{ h: number | null }>("SELECT default_ceiling_height_inches AS h FROM floors WHERE id = ?", [floorId]);
    return rows[0]?.h === null || rows[0]?.h === undefined ? null : Number(rows[0].h);
  }

  /** A room's typed size changed: bound quantities follow it (spec §86). */
  async refreshRooms(roomIds: string[]): Promise<void> {
    const featureIds = await this.refreshBoundFeatures(roomIds);
    this.emit({ floorId: null, changedRoomIds: roomIds, regeneratedFeatureIds: featureIds });
  }

  /** Room measurement reconciliation: use geometry or keep the manual override. */
  async reconcileRoomMeasurement(roomId: string, measurement: "floorArea" | "perimeter", choice: "geometry" | "override"): Promise<void> {
    await this.rooms.setMeasurementSource(roomId, measurement, choice);
    const featureIds = await this.refreshBoundFeatures([roomId]);
    this.emit({ floorId: null, changedRoomIds: [roomId], regeneratedFeatureIds: featureIds });
  }
}

export interface ScopeSyncEvent {
  /** What an Existing edit did to the project's plans (spec §60H). */
  upstream?: UpstreamSyncOutcome | null;
  floorId: string | null;
  changedRoomIds: string[];
  regeneratedFeatureIds: string[];
}

/**
 * The value a geometry-bound feature follows: the room's EFFECTIVE measurement
 * (geometry, or the room's explicit manual override when the user kept it).
 * Null when the room has no usable value.
 */
export function boundQuantityValue(
  room: Room | null,
  geometry: RoomGeometry | null,
  binding: FeatureGeometryBinding,
  floorDefaultWallHeightInches: number | null = null,
): number | null {
  if (!room) return null;
  // Only ROOM bindings are measured from a room. Object, change, finish and
  // door-replacement quantities have their own reconcilers; §89 found this
  // function used to hand every non-floor binding the room's PERIMETER.
  if (!isRoomBinding(binding)) return null;
  if (binding === "room-wall-area") return effectiveWallArea(room, geometry, floorDefaultWallHeightInches).valueX100;
  // A flat ceiling's surface is its floor's (§82-18) — the same measurement, named for what is painted.
  // Spec §86: the EFFECTIVE measurement — drawn geometry, a kept override,
  // or, for a room nobody has drawn, the size typed for it. Phase 7 counted
  // geometry and overrides only, so a hand-measured bathroom's flooring and
  // paint stayed blank and a package priced only its fixtures (found in
  // hosted acceptance). Drawing the room later still wins, by the same rule.
  const eff = binding === "room-perimeter" ? effectivePerimeter(room, geometry) : effectiveFloorArea(room, geometry);
  return eff.valueX100;
}
